import { useEffect, useState } from "react";
import {
    Wallet, IndianRupee, Receipt, AlertCircle, RefreshCw,
    CheckCircle2, X, Loader2, FileText, TrendingUp,
} from "lucide-react";
import { api, API, inr } from "../../lib/api";
import { toast } from "sonner";
import AdminShopPicker from "../../components/AdminShopPicker";
import { openOrDownloadPdf, openPdfUrl, filenameFromContentDisposition } from "../../lib/pdfDownload";
import PdfViewerModal from "../../lib/PdfViewerModal";

const WINDOW_OPTIONS = [
    { v: 7, label: "Last 7 days" },
    { v: 30, label: "Last 30 days" },
    { v: 90, label: "Last 90 days" },
];

export default function AdminSettlements() {
    const [summary, setSummary] = useState(null);
    const [byShop, setByShop] = useState([]);
    const [days, setDays] = useState(30);
    const [busy, setBusy] = useState(false);
    const [drawerShop, setDrawerShop] = useState(null);   // { restaurant_id, restaurant_name }
    const [drawerRows, setDrawerRows] = useState([]);
    const [drawerBusy, setDrawerBusy] = useState(false);
    // iter84 — searchable per-shop narrower; "" = show every shop.
    const [shopFilter, setShopFilter] = useState("");
    // iter134m — native in-app PDF viewer state.
    const [pdfModal, setPdfModal] = useState(null);

    const filteredByShop = shopFilter
        ? byShop.filter((row) => row.restaurant_id === shopFilter)
        : byShop;

    const load = async () => {
        try {
            const [{ data: s }, { data: bs }] = await Promise.all([
                api.get(`/admin/settlements/summary?days=${days}`),
                api.get(`/admin/settlements/by-shop?days=${days}`),
            ]);
            setSummary(s);
            setByShop(bs.shops || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load settlements");
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

    const runSettlement = async () => {
        // iter134e6 — use the currently-selected window instead of a
        // hardcoded 7 days. The confirm dialog + the ``days_back``
        // parameter must both reflect what the operator sees in the
        // filter dropdown.
        if (!window.confirm(`Generate PENDING settlements for the last ${days} days?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/settlements/run?days_back=${days}`);
            toast.success(`Created ${data.created} new settlement${data.created === 1 ? "" : "s"}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };

    // iter134e2 — one-click backfill of legacy period-rollup
    // settlements into per-order rows. Safe to re-run (idempotent;
    // PAID rows are audit-immutable and left untouched). Shows a
    // dry-run preview first so the admin sees the impact before
    // committing.
    const backfillLegacy = async () => {
        setBusy(true);
        try {
            const { data: preview } = await api.post(`/admin/settlements/backfill-legacy?dry_run=true`);
            if (!preview.legacy_scanned) {
                toast.success("No legacy rollup settlements found — nothing to do.");
                return;
            }
            const ok = window.confirm(
                `Backfill ${preview.legacy_scanned} legacy rollup settlement(s) into ` +
                `${preview.per_order_created} per-order rows? ` +
                `Rollups will be deleted (PAID rows are never touched).`
            );
            if (!ok) return;
            const { data } = await api.post(`/admin/settlements/backfill-legacy`);
            toast.success(
                `Migrated ${data.rollups_deleted} rollup(s) → ` +
                `${data.per_order_created} per-order settlement(s)` +
                (data.orders_missing ? ` · ${data.orders_missing} orphaned` : "")
            );
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Backfill failed");
        } finally { setBusy(false); }
    };

    const openDrawer = async (row) => {
        setDrawerShop(row);
        setDrawerBusy(true);
        try {
            // iter134e2 — use the SAME window as the parent table so
            // the "N settlements" count on the shop row matches the
            // rows shown in the drawer. Previously hardcoded to 90
            // days which produced a confusing mismatch (parent "1
            // settlement" vs drawer "4 rows").
            const { data } = await api.get(`/admin/settlements?shop_id=${row.restaurant_id}&days=${days}`);
            setDrawerRows(Array.isArray(data) ? data : []);
        } catch { setDrawerRows([]); }
        finally { setDrawerBusy(false); }
    };
    const closeDrawer = () => { setDrawerShop(null); setDrawerRows([]); };

    const markPaid = async (sid) => {
        const ref = window.prompt("Payment reference (UPI ID / bank txn ID — optional)") || "";
        try {
            await api.patch(`/admin/settlements/${sid}/mark-paid?payout_ref=${encodeURIComponent(ref)}`);
            toast.success("Marked PAID");
            // iter106 — refresh the parent by-shop table first, then reopen
            // the drawer with the FRESHLY-computed shop row (updated
            // pending/paid totals). Passing the stale drawerShop skipped
            // the header refresh and caused the "screen looks blank"
            // impression when the pending count silently dropped to 0.
            const shopId = drawerShop?.restaurant_id;
            const bsResp = await api.get(`/admin/settlements/by-shop?days=${days}`);
            const fresh = bsResp.data;
            setByShop(fresh.shops || []);
            if (shopId) {
                const nextShop = (fresh.shops || []).find((s) => s.restaurant_id === shopId);
                if (nextShop) await openDrawer(nextShop);
                else await openDrawer({ ...drawerShop, pending_amount: 0 });
            }
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    // iter134e5 — Fix a stuck legacy rollup settlement (PAID or
    // PENDING) by voiding it and regenerating per-order rows so the
    // math + payout method reflect the actual per-order CDC /
    // menu-price-uplift logic. Only enabled on rows without
    // ``order_id`` — per-order rows don't need this.
    const voidAndRegenerate = async (s) => {
        const ok = window.confirm(
            `This will VOID settlement ${s.id.slice(0, 8)}… and regenerate ` +
            `one per-order settlement for each delivered order in its window. ` +
            `The voided row stays in the ledger for audit. Continue?`
        );
        if (!ok) return;
        try {
            const { data } = await api.post(`/admin/settlements/${s.id}/void-and-regenerate`);
            toast.success(
                `Voided legacy row · created ${data.per_order_created} per-order settlement(s)`
            );
            const shopId = drawerShop?.restaurant_id;
            const bsResp = await api.get(`/admin/settlements/by-shop?days=${days}`);
            setByShop(bsResp.data.shops || []);
            if (shopId) {
                const nextShop = (bsResp.data.shops || []).find((x) => x.restaurant_id === shopId);
                if (nextShop) await openDrawer(nextShop);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to void & regenerate");
        }
    };

    // iter101 — download the PDF invoice via authenticated axios call.
    // iter134 — Delegates to the shared ``openOrDownloadPdf`` helper
    // which handles the Capacitor Android WebView case (previously
    // silently a no-op) by routing through ``navigator.share`` when
    // available. See ``lib/pdfDownload.js``.
    const downloadInvoice = async (sid) => {
        // iter134m — native: render inline via PdfViewerModal.
        const cap = typeof window !== "undefined" ? window.Capacitor : null;
        if (cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform()) {
            setPdfModal({ endpoint: `/admin/settlements/${sid}/invoice.pdf`, filename: `payout-${sid}.pdf` });
            return;
        }
        try {
            const { data, headers } = await api.get(
                `/admin/settlements/${sid}/invoice.pdf`,
                { responseType: "blob" },
            );
            const ctype = (headers["content-type"] || "").toLowerCase();
            if (!ctype.includes("application/pdf")) {
                // Backend returned something else — likely a JSON error
                // that axios blob-mode couldn't parse. Read it as text.
                const text = await data.text();
                toast.error(text?.slice(0, 200) || "Server did not return a PDF");
                return;
            }
            const blob = new Blob([data], { type: "application/pdf" });
            const filename = filenameFromContentDisposition(
                headers["content-disposition"], `payout-${sid}.pdf`,
            );
            await openOrDownloadPdf(blob, filename);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to download invoice");
        }
    };

    const markAllPaid = async (shop) => {
        const pending = shop.pending_amount;
        if (!pending) return toast.info("Nothing pending for this shop");
        if (!window.confirm(`Mark ALL pending payouts for ${shop.restaurant_name} as PAID? (${inr(pending)})`)) return;
        const ref = window.prompt("Payment reference (UPI ID / bank txn ID — optional)") || "";
        try {
            const { data } = await api.post(
                `/admin/settlements/mark-all-paid?shop_id=${shop.restaurant_id}&payout_ref=${encodeURIComponent(ref)}`,
            );
            toast.success(`Marked ${data.marked_paid} settlement${data.marked_paid === 1 ? "" : "s"} PAID`);
            // iter106 — refresh parent + drawer header consistently after
            // bulk mark-paid so the shop row/drawer don't show stale totals.
            const bsResp = await api.get(`/admin/settlements/by-shop?days=${days}`);
            const fresh = bsResp.data;
            setByShop(fresh.shops || []);
            if (drawerShop?.restaurant_id === shop.restaurant_id) {
                const nextShop = (fresh.shops || []).find((s) => s.restaurant_id === shop.restaurant_id);
                if (nextShop) await openDrawer(nextShop);
            }
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    return (
        <div data-testid="admin-settlements">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold inline-flex items-center gap-2">
                        <Wallet size={22} /> Shop Settlements
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Money you owe each shop after deducting your commission. Mark settlements PAID when you wire the money.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <AdminShopPicker
                        testIdPrefix="settlements-shop"
                        value={shopFilter}
                        onChange={setShopFilter}
                        allShopsOption
                    />
                    <select data-testid="settlements-window"
                        value={days} onChange={(e) => setDays(Number(e.target.value))}
                        className="bg-card border border-border rounded-xl px-3 py-2 text-sm font-semibold">
                        {WINDOW_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                    <button data-testid="settlements-run"
                        onClick={runSettlement} disabled={busy}
                        className="bg-foreground text-background rounded-xl px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                        Generate new
                    </button>
                    <button data-testid="settlements-backfill-legacy"
                        onClick={backfillLegacy} disabled={busy}
                        title="Convert legacy period-rollup settlements into per-order rows"
                        className="border border-amber-300 bg-amber-50 text-amber-900 rounded-xl px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50 hover:bg-amber-100">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                        Backfill legacy
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
                <Kpi
                    icon={<AlertCircle size={16} />}
                    label="Owed to shops now"
                    value={summary ? inr(summary.pending_amount) : "—"}
                    klass="border-red-200 bg-red-50 text-red-900"
                    testid="kpi-pending"
                />
                <Kpi
                    icon={<CheckCircle2 size={16} />}
                    label={`Paid in last ${days}d`}
                    value={summary ? inr(summary.paid_amount) : "—"}
                    klass="border-emerald-200 bg-emerald-50 text-emerald-900"
                    testid="kpi-paid"
                />
                <Kpi
                    icon={<TrendingUp size={16} />}
                    label="Your commission earned"
                    value={summary ? inr(summary.commission_earned) : "—"}
                    klass="border-violet-200 bg-violet-50 text-violet-900"
                    testid="kpi-commission"
                />
                <Kpi
                    icon={<Receipt size={16} />}
                    label="Shops with pending"
                    value={summary ? String(summary.shops_with_pending) : "—"}
                    klass="border-amber-200 bg-amber-50 text-amber-900"
                    testid="kpi-shops-pending"
                />
            </div>

            <div className="mt-6 bg-card border border-border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                    <thead>
                        <tr className="bg-muted/60 text-left text-[11px] uppercase tracking-wider">
                            <th className="px-4 py-3">Shop</th>
                            <th className="px-4 py-3 text-right">Orders</th>
                            <th className="px-4 py-3 text-right">Gross sales</th>
                            <th className="px-4 py-3 text-right">Commission (%)</th>
                            <th className="px-4 py-3 text-right">Owed now</th>
                            <th className="px-4 py-3 text-right">Paid</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredByShop.map((row) => (
                            <tr key={row.restaurant_id} data-testid={`shop-row-${row.restaurant_id}`} className="border-t border-border">
                                <td className="px-4 py-3">
                                    <p className="font-semibold">{row.restaurant_name}</p>
                                    <p className="text-[11px] text-muted-foreground">{row.settlement_count} settlement{row.settlement_count === 1 ? "" : "s"}</p>
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums">{row.orders_count}</td>
                                <td className="px-4 py-3 text-right tabular-nums font-semibold">{inr(row.gross_sales)}</td>
                                <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{inr(row.commission_amount)} <span className="text-[10px]">({row.commission_percent}%)</span></td>
                                <td className="px-4 py-3 text-right tabular-nums">
                                    {row.pending_amount > 0 ? (
                                        <span className="font-bold text-red-700">{inr(row.pending_amount)}</span>
                                    ) : (
                                        <span className="text-emerald-700 inline-flex items-center gap-0.5"><CheckCircle2 size={12} /> Clear</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{inr(row.paid_amount)}</td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <button data-testid={`view-${row.restaurant_id}`}
                                        onClick={() => openDrawer(row)}
                                        className="text-xs font-bold text-primary inline-flex items-center gap-1">
                                        <FileText size={11} /> Ledger
                                    </button>
                                    {row.pending_amount > 0 && (
                                        <button data-testid={`mark-all-${row.restaurant_id}`}
                                            onClick={() => markAllPaid(row)}
                                            className="ml-3 text-xs font-bold text-emerald-700 inline-flex items-center gap-1">
                                            <IndianRupee size={11} /> Mark all paid
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {filteredByShop.length === 0 && (
                            <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">
                                {shopFilter
                                    ? "No settlements for the selected shop in this window."
                                    : "No settlements in this window. Click "}
                                {!shopFilter && <><b>Generate new</b> to roll up the last 7 days of delivered orders.</>}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Per-shop ledger drawer */}
            {drawerShop && (
                <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={closeDrawer} data-testid="settlement-drawer">
                    <div className="w-full max-w-2xl bg-card h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="sticky top-0 bg-card border-b border-border px-5 py-4 flex items-center justify-between">
                            <div>
                                <h2 className="font-bold text-lg">{drawerShop.restaurant_name}</h2>
                                <p className="text-xs text-muted-foreground">
                                    Owed now: <b className="text-red-700">{inr(drawerShop.pending_amount)}</b>
                                    {" · "}Paid: <b className="text-emerald-700">{inr(drawerShop.paid_amount)}</b>
                                </p>
                            </div>
                            <button onClick={closeDrawer} className="p-2 hover:bg-muted rounded-lg"><X size={18} /></button>
                        </div>
                        {drawerBusy ? (
                            <div className="py-16 text-center"><Loader2 size={24} className="animate-spin mx-auto" /></div>
                        ) : (
                            <>
                            {/* Desktop table — hidden on mobile */}
                            <table className="w-full text-sm hidden md:table">
                                <thead>
                                    <tr className="bg-muted/60 text-left text-[11px] uppercase tracking-wider">
                                        <th className="px-4 py-3">Period</th>
                                        <th className="px-4 py-3 text-right">Orders</th>
                                        <th className="px-4 py-3 text-right">Gross</th>
                                        <th className="px-4 py-3 text-right">Net payable</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {drawerRows.map((s) => {
                                        const ps = (s.period_start || "").slice(0, 10);
                                        return (
                                            <tr key={s.id} data-testid={`settlement-${s.id}`} className="border-t border-border">
                                                <td className="px-4 py-3 whitespace-nowrap">{ps}</td>
                                                <td className="px-4 py-3 text-right">{s.orders_count}</td>
                                                <td className="px-4 py-3 text-right tabular-nums">{inr(s.gross_sales)}</td>
                                                <td className="px-4 py-3 text-right tabular-nums font-bold">{inr(s.payable_amount)}</td>
                                                <td className="px-4 py-3">
                                                    {s.status === "PAID"
                                                        ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">PAID</span>
                                                        : <span className="text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">PENDING</span>}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {/* iter101 — PDF invoice download (admin side) */}
                                                    <button
                                                        data-testid={`invoice-${s.id}`}
                                                        onClick={() => downloadInvoice(s.id)}
                                                        className="text-xs font-bold text-primary inline-flex items-center gap-1 mr-3"
                                                    >
                                                        <FileText size={11} /> PDF
                                                    </button>
                                                    {s.status === "PENDING" && (
                                                        <button data-testid={`mark-${s.id}`}
                                                            onClick={() => markPaid(s.id)}
                                                            className="text-xs font-bold text-emerald-700">Mark paid</button>
                                                    )}
                                                    {!s.order_id && s.status !== "VOIDED" && (
                                                        <button data-testid={`void-regen-${s.id}`}
                                                            onClick={() => voidAndRegenerate(s)}
                                                            title="Legacy rollup — void & regenerate as per-order settlements"
                                                            className="text-xs font-bold text-amber-700 ml-3">Fix legacy</button>
                                                    )}
                                                    {s.payout_ref && (
                                                        <p className="text-[10px] text-muted-foreground mt-0.5">Ref: {s.payout_ref}</p>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {drawerRows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No settlements yet</td></tr>}
                                </tbody>
                            </table>
                            {/* Mobile card layout — visible below md */}
                            <div className="md:hidden divide-y divide-border">
                                {drawerRows.length === 0 && (
                                    <div className="text-center py-10 text-muted-foreground text-sm">No settlements yet</div>
                                )}
                                {drawerRows.map((s) => {
                                    const ps = (s.period_start || "").slice(0, 10);
                                    return (
                                        <div key={s.id} data-testid={`settlement-${s.id}`} className="p-3">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Period</p>
                                                    <p className="text-sm font-semibold">{ps}</p>
                                                    <p className="text-[11px] text-muted-foreground mt-0.5">
                                                        {s.orders_count} order{s.orders_count === 1 ? "" : "s"} · Gross <span className="tabular-nums font-semibold text-foreground">{inr(s.gross_sales)}</span>
                                                    </p>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Net payable</p>
                                                    <p className="text-base font-bold tabular-nums leading-tight">{inr(s.payable_amount)}</p>
                                                    <div className="mt-1">
                                                        {s.status === "PAID"
                                                            ? <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">PAID</span>
                                                            : <span className="text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">PENDING</span>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="mt-2 flex items-center justify-end gap-2">
                                                <button
                                                    data-testid={`invoice-${s.id}`}
                                                    onClick={() => downloadInvoice(s.id)}
                                                    className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-semibold active:scale-95 transition-transform"
                                                >
                                                    <FileText size={12} /> PDF
                                                </button>
                                                {s.status === "PENDING" && (
                                                    <button
                                                        data-testid={`mark-${s.id}`}
                                                        onClick={() => markPaid(s.id)}
                                                        className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white px-3 py-1.5 text-xs font-bold active:scale-95 transition-transform"
                                                    >
                                                        Mark paid
                                                    </button>
                                                )}
                                                {!s.order_id && s.status !== "VOIDED" && (
                                                    <button
                                                        data-testid={`void-regen-${s.id}`}
                                                        onClick={() => voidAndRegenerate(s)}
                                                        title="Legacy rollup — void & regenerate as per-order settlements"
                                                        className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-1.5 text-xs font-bold active:scale-95 transition-transform"
                                                    >
                                                        Fix legacy
                                                    </button>
                                                )}
                                            </div>
                                            {s.payout_ref && (
                                                <p className="text-[10px] text-muted-foreground text-right mt-1">Ref: {s.payout_ref}</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            {pdfModal && (
                <PdfViewerModal
                    endpoint={pdfModal.endpoint}
                    filename={pdfModal.filename}
                    onClose={() => setPdfModal(null)}
                />
            )}
        </div>
    );
}

function Kpi({ icon, label, value, klass, testid }) {
    return (
        <div data-testid={testid} className={`border rounded-2xl p-4 ${klass}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1.5">
                {icon} {label}
            </div>
            <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
    );
}
